import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { appConfig } from 'shared/appConfig';
import { useSignRealtimerAccessTokenMutation } from 'shared/hooks/api/signRealtimerAccessToken/signRealtimerAccessToken.generated';
import { useAuthStore } from 'shared/hooks/stores/useAuthStore';
import io from 'socket.io-client';
import {
  RealtimerProviderContext,
  RealtimerProviderContextValue,
} from './RealtimerProviderContext';

const connection = appConfig.realtimerUrl
  ? io(appConfig.realtimerUrl, {
      autoConnect: false,
      extraHeaders: {
        'target-service': 'realtimer',
      },
    })
  : undefined;

export const RealtimerProvider = ({ children }: PropsWithChildren) => {
  const [isConnected, setIsConnected] = useState(false);
  const authenticated = useAuthStore((state) => state.authenticated);

  const subscribe = useCallback((audience: string, callback: (message: any) => any) => {
    if (!connection) {
      return;
    }

    connection.on(audience, callback);
    connection.emit('subscribe', { audience });
  }, []);

  const unsubscribe = useCallback((audience: string, callback: (message: any) => any) => {
    if (!connection) {
      return;
    }

    connection.off(audience, callback);
    connection.emit('unsubscribe', { audience });
  }, []);

  const [signAccessToken] = useSignRealtimerAccessTokenMutation();

  const signAccessTokenAndConnect = useCallback(() => {
    if (!connection) {
      return;
    }

    connection.on('connect', () => setIsConnected(true));
    connection.on('disconnect', () => setIsConnected(false));

    if (connection.connected) {
      return;
    }

    signAccessToken()
      .then((result) => {
        const token = result.data?.signRealtimerAccessToken;
        if (!token) {
          return;
        }

        connection.auth = { token };
        connection.connect();
      })
      .catch((error) => {
        console.error(`Error while signing RealtimerAccessToken`, error);
      });
  }, [signAccessToken, setIsConnected]);

  useEffect(() => {
    if (isConnected && !authenticated) {
      connection?.disconnect();
    }

    if (isConnected || !authenticated) {
      return;
    }

    signAccessTokenAndConnect();
  }, [isConnected, authenticated]);

  const value: RealtimerProviderContextValue = useMemo(
    () => ({
      subscribe,
      unsubscribe,
      isConnected,
    }),
    [subscribe, unsubscribe, isConnected],
  );

  return (
    <RealtimerProviderContext.Provider value={value}>{children}</RealtimerProviderContext.Provider>
  );
};
